import React, { useContext, useState } from 'react';
import { Edit, Loader2, LogOut, ParkingSquare } from 'lucide-react';

import { Aide } from '@/components/ui/aide';
import { AuthContext } from '../../context/AuthContext';
import { tripsAPI } from '../../lib/api';
import { messageErreur } from '../../lib/erreurs';
import { formatDate } from '../../lib/formats';
import { libelle } from '../../lib/libelles';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../ui/sonner';

const nouvelleCle = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `chargement-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const jour = (valeur) => (valeur ? String(valeur).slice(0, 10) : '');

const enParking = (trip) => Boolean(trip?.parked_at) && !trip?.parking_left_at;

const formulaireDe = (trip) => ({
  loading_depot: trip?.loading_depot || '',
  loading_voucher_number: trip?.loading_voucher_number || '',
  loading_date: jour(trip?.loading_date),
  exit_document_number: trip?.exit_document_number || '',
  customs_exit_date: jour(trip?.customs_exit_date),
});

const CHAMPS = [
  { cle: 'loading_depot', label: 'Dépôt chargeur' },
  { cle: 'loading_voucher_number', label: 'N° de bon' },
  { cle: 'loading_date', label: 'Date de chargement', type: 'date' },
  { cle: 'exit_document_number', label: 'Document de sortie' },
  { cle: 'customs_exit_date', label: 'Sortie douane', type: 'date' },
];

const Valeur = ({ label, children, testid }) => (
  <div className="min-w-0" data-testid={testid}>
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className="break-words text-sm font-medium">{children}</dd>
  </div>
);

/**
 * Carte « Chargement et sortie » de la fiche trajet (lot 056): dépôt, N° de
 * bon, date de chargement, document de sortie, sortie douane et parking.
 * Modifiable par qui peut écrire les trajets.
 */
const TripLoadingInfoCard = ({ trip, onChanged }) => {
  const auth = useContext(AuthContext);
  const peutEcrire = !auth
    || (typeof auth.canWriteModule === 'function' && auth.canWriteModule('trips'))
    || auth.user?.role === 'LOGISTIQUE';
  const [ouvert, setOuvert] = useState(false);
  const [form, setForm] = useState(() => formulaireDe(trip));
  const [cle, setCle] = useState(nouvelleCle);
  const [depots, setDepots] = useState([]);
  const [enregistrement, setEnregistrement] = useState(false);
  const [parkingEnCours, setParkingEnCours] = useState(false);

  if (!trip?.id) return null;

  const ouvrir = () => {
    setForm(formulaireDe(trip));
    setCle(nouvelleCle());
    setOuvert(true);
    tripsAPI
      .getLoadingDepots()
      .then((liste) => setDepots(Array.isArray(liste) ? liste : []))
      .catch(() => setDepots([]));
  };

  const enregistrer = async (event) => {
    event?.preventDefault?.();
    if (enregistrement) return;
    const initial = formulaireDe(trip);
    const changes = {};
    CHAMPS.forEach(({ cle: nom }) => {
      const valeur = String(form[nom] || '').trim();
      if (valeur !== initial[nom]) changes[nom] = valeur || null;
    });
    if (Object.keys(changes).length === 0) {
      setOuvert(false);
      return;
    }
    setEnregistrement(true);
    try {
      await tripsAPI.updateLoadingInfo(trip.id, { ...changes, idempotency_key: cle });
      toast.success('Chargement et sortie enregistrés');
      setOuvert(false);
      await onChanged?.({ silencieux: true });
    } catch (err) {
      toast.error(messageErreur(err, "Les informations n'ont pas pu être enregistrées"));
    } finally {
      setEnregistrement(false);
    }
  };

  const basculerParking = async () => {
    if (parkingEnCours) return;
    const action = enParking(trip) ? 'LEAVE' : 'ENTER';
    setParkingEnCours(true);
    try {
      const resultat = await tripsAPI.setParking({
        tripIds: [trip.id],
        action,
        idempotencyKey: nouvelleCle(),
      });
      if ((resultat?.refused || []).length) {
        toast.error('Un trajet livré, clôturé ou annulé ne va pas en parking');
      } else {
        toast.success(action === 'ENTER' ? 'Trajet mis en parking' : 'Trajet sorti du parking');
      }
      await onChanged?.({ silencieux: true });
    } catch (err) {
      toast.error(messageErreur(err, "Le parking n'a pas pu être mis à jour"));
    } finally {
      setParkingEnCours(false);
    }
  };

  const parke = enParking(trip);
  const termine = ['DELIVERED', 'COMPLETED', 'CANCELLED'].includes(String(trip.status || '').toUpperCase());

  return (
    <section
      className="flex flex-col gap-3 rounded-xl border border-border bg-card px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
      data-testid="chargement-sortie"
    >
      <h2 className="sr-only">Chargement et sortie</h2>
      <dl className="grid min-w-0 flex-1 grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 lg:grid-cols-6">
        <Valeur label="Dépôt" testid="chargement-depot">{trip.loading_depot || '—'}</Valeur>
        <Valeur label="N° de bon">{trip.loading_voucher_number || '—'}</Valeur>
        <Valeur label="Chargement">{formatDate(trip.loading_date)}</Valeur>
        <Valeur label="Doc. de sortie">{trip.exit_document_number || '—'}</Valeur>
        <Valeur label="Sortie douane" testid="chargement-sortie-douane">
          <span className="inline-flex items-center gap-1">
            {formatDate(trip.customs_exit_date)}
            {trip.customs_exit_date && trip.customs_exit_source ? (
              <Aide titre="Sortie douane">{libelle('source_sortie_douane', trip.customs_exit_source)}</Aide>
            ) : null}
          </span>
        </Valeur>
        <Valeur label="Parking" testid="chargement-parking">
          {parke ? `${libelle('parking_etat', 'oui')} · ${formatDate(trip.parked_at)}` : libelle('parking_etat', 'non')}
        </Valeur>
      </dl>
      {peutEcrire ? (
        <div className="flex shrink-0 flex-wrap gap-1">
          {parke || !termine ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={basculerParking}
              disabled={parkingEnCours}
              data-testid="chargement-parking-btn"
            >
              {parkingEnCours ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : parke ? (
                <LogOut className="h-4 w-4" aria-hidden="true" />
              ) : (
                <ParkingSquare className="h-4 w-4" aria-hidden="true" />
              )}
              {libelle('parking_action', parke ? 'LEAVE' : 'ENTER')}
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" onClick={ouvrir} data-testid="chargement-modifier">
            <Edit className="h-4 w-4" aria-hidden="true" />
            Modifier
          </Button>
        </div>
      ) : null}

      <Dialog open={ouvert} onOpenChange={(valeur) => !enregistrement && setOuvert(valeur)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Chargement et sortie</DialogTitle>
            <DialogDescription className="sr-only">
              Dépôt, numéro de bon, date de chargement, document de sortie et date de sortie douane.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={enregistrer} className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="chargement-form">
            {CHAMPS.map(({ cle: nom, label, type }) => (
              <div key={nom} className="space-y-1">
                <Label htmlFor={`chargement-${nom}`}>{label}</Label>
                <Input
                  id={`chargement-${nom}`}
                  type={type || 'text'}
                  value={form[nom]}
                  list={nom === 'loading_depot' ? 'chargement-depots' : undefined}
                  autoComplete="off"
                  maxLength={type ? undefined : nom === 'loading_depot' ? 120 : 100}
                  onChange={(e) => {
                    const valeur = nom === 'loading_depot' ? e.target.value.toUpperCase() : e.target.value;
                    setForm((avant) => ({ ...avant, [nom]: valeur }));
                  }}
                  className="input-base"
                />
              </div>
            ))}
            <datalist id="chargement-depots">
              {depots.map((depot) => (
                <option key={depot} value={depot} />
              ))}
            </datalist>
            <DialogFooter className="sm:col-span-2">
              <Button type="button" variant="outline" onClick={() => setOuvert(false)} disabled={enregistrement}>
                Annuler
              </Button>
              <Button type="submit" disabled={enregistrement} data-testid="chargement-enregistrer">
                {enregistrement ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                Enregistrer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default TripLoadingInfoCard;
